import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CrmLayout } from "@/components/crm/CrmLayout";
import { api, apiError } from "@/lib/api";
import { PIPELINE, PIPELINE_MAP, needLabel } from "@/lib/crmConstants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, CheckCircle2, XCircle, Loader2, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function ProspectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const readOnly = user.role === "read_only";
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [advisors, setAdvisors] = useState([]);
  const [task, setTask] = useState({ title: "", due_date: "", kind: "rappel" });
  const [interaction, setInteraction] = useState({ kind: "appel", content: "", outcome: "" });

  const load = useCallback(() => api.get(`/crm/prospects/${id}`).then((r) => setData(r.data)), [id]);
  useEffect(() => {
    load();
    api.get("/crm/users").then((r) => setAdvisors(r.data)).catch(() => {});
  }, [load]);

  if (!data) return <CrmLayout><div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-blue-600" /></div></CrmLayout>;
  const p = data.prospect;
  const ans = data.simulations[0]?.answers || {};

  const patch = async (payload) => {
    try { await api.patch(`/crm/prospects/${id}`, payload); toast.success("Mis à jour."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addNote = async () => { if (!note.trim()) return; await api.post(`/crm/prospects/${id}/notes`, { content: note }); setNote(""); toast.success("Note ajoutée."); load(); };
  const addTask = async () => { if (!task.title.trim()) return; await api.post(`/crm/prospects/${id}/tasks`, task); setTask({ title: "", due_date: "", kind: "rappel" }); toast.success("Tâche créée."); load(); };
  const addInteraction = async () => { if (!interaction.content.trim()) return; await api.post(`/crm/prospects/${id}/interactions`, interaction); setInteraction({ kind: "appel", content: "", outcome: "" }); toast.success("Interaction enregistrée."); load(); };
  const toggleTask = async (t) => { await api.patch(`/crm/tasks/${t.id}`, { done: !t.done }); load(); };

  return (
    <CrmLayout>
      <Button variant="ghost" onClick={() => nav("/espace-courtier/prospects")} className="mb-3"><ArrowLeft size={16} className="mr-1" />Retour</Button>
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left: identity + commercial */}
        <div className="space-y-5">
          <Card className="p-5" data-testid="prospect-identity">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-slate-900">{[p.first_name, p.last_name].filter(Boolean).join(" ") || "Prospect"}</h2>
              <Badge variant="secondary">{needLabel(p.need_type)}</Badge>
            </div>
            <p className="font-mono-crm text-xs text-slate-400 mt-1">{p.id}</p>
            <div className="mt-4 space-y-2 text-sm">
              <p className="flex items-center gap-2 text-slate-700"><Mail size={15} />{p.email}
                {p.email_verified ? <CheckCircle2 size={15} className="text-emerald-600" /> : <XCircle size={15} className="text-slate-300" />}</p>
              {p.phone && <p className="flex items-center gap-2 text-slate-700"><Phone size={15} />{p.phone}</p>}
              <p className="text-slate-500 text-xs">Créé le {new Date(p.created_at).toLocaleString("fr-FR")}</p>
              {p.email_verified_at && <p className="text-emerald-700 text-xs">E-mail vérifié le {new Date(p.email_verified_at).toLocaleString("fr-FR")}</p>}
              <p className="text-slate-500 text-xs">Source : {p.source}</p>
              {p.budget && <p className="text-slate-700 text-sm">Budget indicatif : <strong>{p.budget} €</strong></p>}
              {p.destination && <p className="text-slate-700 text-sm">Destination : <strong>{p.destination}</strong></p>}
              {p.effective_date && <p className="text-slate-700 text-sm">Date d'effet : {p.effective_date}</p>}
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <h3 className="font-semibold text-slate-900">Suivi commercial</h3>
            <div><Label className="mb-2 block text-xs">Statut</Label>
              <Select value={p.status} onValueChange={(v) => patch({ status: v })} disabled={readOnly}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PIPELINE.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-2 block text-xs">Conseiller responsable</Label>
              <Select value={p.assigned_advisor || "none"} onValueChange={(v) => patch({ assigned_advisor: v === "none" ? "" : v })} disabled={readOnly}>
                <SelectTrigger data-testid="advisor-select"><SelectValue placeholder="Non affecté" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Non affecté</SelectItem>{advisors.map((a) => <SelectItem key={a.id} value={a.email}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-2 block text-xs">Prochaine action</Label>
              <Input defaultValue={p.next_action} onBlur={(e) => e.target.value !== p.next_action && patch({ next_action: e.target.value })} disabled={readOnly} data-testid="next-action" /></div>
            {p.status === "perdu" && (
              <div><Label className="mb-2 block text-xs">Motif de perte</Label>
                <Input defaultValue={p.loss_reason} onBlur={(e) => patch({ loss_reason: e.target.value })} disabled={readOnly} /></div>)}
          </Card>
        </div>

        {/* Right: tabs */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="simulation">
            <TabsList><TabsTrigger value="simulation">Simulation</TabsTrigger><TabsTrigger value="notes">Notes</TabsTrigger><TabsTrigger value="taches">Tâches</TabsTrigger><TabsTrigger value="historique">Historique</TabsTrigger></TabsList>

            <TabsContent value="simulation">
              <Card className="p-5">
                <h3 className="font-semibold text-slate-900 mb-3">Réponses au simulateur</h3>
                {Object.keys(ans).length === 0 ? <p className="text-slate-400 text-sm">Aucune simulation.</p> : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {Object.entries(ans).map(([k, v]) => (
                      <div key={k} className="border rounded-lg p-3"><p className="text-xs text-slate-400 uppercase">{k.replace(/_/g, " ")}</p><p className="text-sm text-slate-800 font-medium mt-0.5">{Array.isArray(v) ? v.join(", ") : (v && typeof v === "object" ? Object.entries(v).map(([pk, pv]) => `${pk.replace(/_/g, " ")}: ${pv}`).join(" · ") : String(v))}</p></div>))}
                  </div>)}
              </Card>
            </TabsContent>

            <TabsContent value="notes">
              <Card className="p-5">
                {!readOnly && <div className="flex gap-2 mb-4"><Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ajouter une note interne..." data-testid="note-input" /><Button onClick={addNote} data-testid="add-note"><Plus size={16} /></Button></div>}
                <div className="space-y-3">{data.notes.map((n) => (
                  <div key={n.id} className="border-l-2 border-blue-500 pl-3 py-1"><p className="text-sm text-slate-800">{n.content}</p><p className="text-xs text-slate-400 mt-0.5">{n.author} · {new Date(n.at).toLocaleString("fr-FR")}</p></div>))}
                  {data.notes.length === 0 && <p className="text-slate-400 text-sm">Aucune note.</p>}</div>
              </Card>
            </TabsContent>

            <TabsContent value="taches">
              <Card className="p-5">
                {!readOnly && (
                  <div className="grid sm:grid-cols-4 gap-2 mb-4">
                    <Input className="sm:col-span-2" value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} placeholder="Intitulé de la tâche" data-testid="task-title" />
                    <Input type="date" value={task.due_date} onChange={(e) => setTask({ ...task, due_date: e.target.value })} data-testid="task-date" />
                    <Button onClick={addTask} data-testid="crm-add-task-button"><Plus size={16} className="mr-1" />Ajouter</Button>
                  </div>)}
                <div className="space-y-2">{data.tasks.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 border rounded-lg p-3">
                    <input type="checkbox" checked={t.done} onChange={() => toggleTask(t)} disabled={readOnly} className="w-4 h-4" data-testid={`task-check-${t.id}`} />
                    <div className="flex-1"><p className={`text-sm ${t.done ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</p><p className="text-xs text-slate-400">{t.due_date || "Sans échéance"} · {t.assigned_to}</p></div>
                    <Badge variant="secondary" className="text-xs">{t.kind}</Badge>
                  </div>))}
                  {data.tasks.length === 0 && <p className="text-slate-400 text-sm">Aucune tâche.</p>}</div>
              </Card>
            </TabsContent>

            <TabsContent value="historique">
              <Card className="p-5">
                {!readOnly && (
                  <div className="grid sm:grid-cols-4 gap-2 mb-4">
                    <Select value={interaction.kind} onValueChange={(v) => setInteraction({ ...interaction, kind: v })}>
                      <SelectTrigger data-testid="interaction-kind"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="appel">Appel</SelectItem><SelectItem value="email">E-mail</SelectItem><SelectItem value="note">Note</SelectItem></SelectContent>
                    </Select>
                    <Input className="sm:col-span-2" value={interaction.content} onChange={(e) => setInteraction({ ...interaction, content: e.target.value })} placeholder="Résultat / contenu" data-testid="interaction-content" />
                    <Button onClick={addInteraction} data-testid="add-interaction"><Plus size={16} className="mr-1" />Enregistrer</Button>
                  </div>)}
                <div className="space-y-3">{data.interactions.map((it) => (
                  <div key={it.id} className="flex gap-3 text-sm"><span className={`w-2 h-2 rounded-full mt-1.5 ${PIPELINE_MAP[p.status]?.color || "bg-blue-500"}`} />
                    <div><p className="text-slate-800"><Badge variant="outline" className="mr-2 text-xs">{it.kind}</Badge>{it.content}</p><p className="text-xs text-slate-400">{it.actor} · {new Date(it.at).toLocaleString("fr-FR")}</p></div></div>))}
                  {data.interactions.length === 0 && <p className="text-slate-400 text-sm">Aucune interaction.</p>}</div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </CrmLayout>
  );
}
